import { useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { OrderHistoryPopup } from '../../components/orders/OrderHistoryPopup'
import { ReturnDecisionModal } from './ReturnDecisionModal'
import { Paginator } from '../../components/molecules/Paginator'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import {
  ORDERSTATUS,
  ORDERSTATUSLABEL,
  ORDERSTATUSTRANSITIONS,
  isReturnBranch,
  type OrderStatus
} from '../../../constants/order-status.constants'
import {
  useOrderManagementOrders,
  type OrderItemListDto,
  type OrderListDto
} from '../../../viewmodels/useOrderManagementOrders'
import type { OrderReturnListDto } from '../../../viewmodels/useOrderManagementReturns'
import { useOrganisations, type OrganisationDto } from '../../../viewmodels/useOrganisations'

/** Synthesise an OrderReturnListDto from an OrderListDto + its active
 *  pending return fields — the Orders list ships every field the modal
 *  needs (customer + org from the row, return id/reason/requested_at
 *  from the LEFT JOIN), so we don't need a second fetch. */
function buildReturnDto(order: OrderListDto): OrderReturnListDto | null {
  if (
    order.active_return_id === null ||
    order.active_return_reason === null ||
    order.active_return_requested_at === null
  ) {
    return null
  }
  return {
    id: order.active_return_id,
    order_id: order.id,
    reason: order.active_return_reason,
    status: 'pending',
    requested_at: order.active_return_requested_at,
    customer_id: order.customer_id,
    customer_first_name: order.customer_first_name,
    customer_last_name: order.customer_last_name,
    customer_email: order.customer_email,
    customer_phone: order.customer_phone,
    organisation_id: order.organisation_id,
    organisation_name: order.organisation_name
  }
}

const FORWARD_TRANSITIONS: OrderStatus[] = [
  ORDERSTATUS.PACKED,
  ORDERSTATUS.SHIPPED,
  ORDERSTATUS.INTRANSIT,
  ORDERSTATUS.OUTFORDELIVERY,
  ORDERSTATUS.DELIVERED
]

function pickNextForward(current: string): OrderStatus | null {
  const allowed = ORDERSTATUSTRANSITIONS[current as OrderStatus] ?? []
  return (allowed.find((n) => FORWARD_TRANSITIONS.includes(n)) as OrderStatus | undefined) ?? null
}

/**
 * /order-management/orders — operator view of every customer order.
 *
 * Flat table — one row per line item. Header fields (placed date,
 * customer name / email / phone, order id) are repeated under each
 * line so the operator can sort or scan by any column without
 * collapsing rows. Multi-item orders span N rows; their first row
 * gets a thin top border that the row groupings visually inherit.
 */
export function OrderManagementOrdersPage(): JSX.Element {
  // '' = "All organisations" (no filter); otherwise the org id.
  const [orgFilter, setOrgFilter] = useState<string>('')
  const vm = useOrderManagementOrders(orgFilter === '' ? null : orgFilter)
  const orgsVm = useOrganisations()
  const [historyOrder, setHistoryOrder] = useState<{ id: string; number: string } | null>(null)
  const [decisionTarget, setDecisionTarget] = useState<
    { row: OrderReturnListDto; decision: 'accept' | 'reject' } | null
  >(null)

  // Index line items by order_id once so the render loop is O(N) not O(N²).
  const itemsByOrder = useMemo(() => {
    const map = new Map<string, OrderItemListDto[]>()
    for (const li of vm.lineItems) {
      const list = map.get(li.order_id)
      if (list === undefined) map.set(li.order_id, [li])
      else list.push(li)
    }
    return map
  }, [vm.lineItems])

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Orders</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            Every customer order. Multi-product orders show one row per product.
          </p>
        </div>
        <label className="block">
          <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">
            Organisation
          </span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            disabled={orgsVm.isLoading}
            className="mt-1 w-64 rounded-md border border-border bg-bg-primary p-2 font-body text-body-sm text-charcoal disabled:bg-bg-secondary disabled:text-text-secondary"
          >
            <option value="">All organisations</option>
            {orgsVm.items.map((o: OrganisationDto) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={vm.reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading && vm.orders.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.orders.length === 0 ? (
        <Placeholder>
          {orgFilter === ''
            ? "No orders yet — customers haven't confirmed any orders."
            : "No orders for this organisation yet."}
        </Placeholder>
      ) : (
        <>
          <TableShell>
            <table className="w-full font-body text-body-sm">
              <thead className="bg-parchment/50 text-text-secondary">
                <tr>
                  <Th>Placed</Th>
                  <Th>Customer</Th>
                  <Th>Product</Th>
                  <Th className="text-right">Qty</Th>
                  <Th className="text-right">Line total</Th>
                  <Th>Status</Th>
                  <Th>Next step</Th>
                </tr>
              </thead>
              <tbody>
                {vm.orders.flatMap((order) => {
                  const items = itemsByOrder.get(order.id) ?? []
                  const openHistory = (): void => setHistoryOrder({ id: order.id, number: `UYM-${order.id.slice(0, 8).toUpperCase()}` })
                  const openDecision = (decision: 'accept' | 'reject'): void => {
                    const row = buildReturnDto(order)
                    if (row !== null) setDecisionTarget({ row, decision })
                  }
                  if (items.length === 0) {
                    return [
                      <EmptyOrderRow
                        key={order.id}
                        order={order}
                        onTransition={vm.transition}
                        onOpenHistory={openHistory}
                        onOpenDecision={openDecision}
                      />
                    ]
                  }
                  return items.map((line, idx) => (
                    <LineRow
                      key={line.id}
                      order={order}
                      line={line}
                      isFirstOfOrder={idx === 0}
                      onTransition={vm.transition}
                      onOpenHistory={openHistory}
                      onOpenDecision={openDecision}
                    />
                  ))
                })}
              </tbody>
            </table>
          </TableShell>
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}
      {historyOrder !== null ? (
        <OrderHistoryPopup
          open={historyOrder !== null}
          orderId={historyOrder.id}
          orderNumber={historyOrder.number}
          scope="operator"
          onClose={() => setHistoryOrder(null)}
        />
      ) : null}
      {decisionTarget !== null ? (
        <ReturnDecisionModal
          open={decisionTarget !== null}
          row={decisionTarget.row}
          decision={decisionTarget.decision}
          onClose={() => setDecisionTarget(null)}
          onConfirm={async (nextStatus, notes) =>
            vm.transitionReturn(decisionTarget.row.id, nextStatus, notes)
          }
        />
      ) : null}
    </div>
  )
}

function customerName(order: OrderListDto): string {
  const name = [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ')
  return name.length > 0 ? name : '—'
}

function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function LineRow({
  order,
  line,
  isFirstOfOrder,
  onTransition,
  onOpenHistory,
  onOpenDecision
}: {
  readonly order: OrderListDto
  readonly line: OrderItemListDto
  readonly isFirstOfOrder: boolean
  readonly onTransition: (orderId: string, nextStatus: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onOpenHistory: () => void
  readonly onOpenDecision: (decision: 'accept' | 'reject') => void
}): JSX.Element {
  const borderClass = isFirstOfOrder ? 'border-t border-border' : 'border-t border-border/40'
  const next = pickNextForward(order.status)
  return (
    <tr className={borderClass}>
      <Td className="whitespace-nowrap text-text-secondary align-top">
        {isFirstOfOrder ? formatDate(order.placed_at) : ''}
      </Td>
      <Td className="align-top">
        {isFirstOfOrder ? (
          <div>
            <div>{customerName(order)}</div>
            <div className="font-mono text-body-xs text-text-secondary">{order.customer_email}</div>
          </div>
        ) : ''}
      </Td>
      <Td>
        <div className="text-charcoal">{line.product_name}</div>
        {line.axes_label !== null ? (
          <div className="text-text-secondary text-body-xs">{line.axes_label}</div>
        ) : null}
      </Td>
      <Td className="text-right whitespace-nowrap font-mono">{line.quantity}</Td>
      <Td className="text-right whitespace-nowrap font-mono">{formatRupees(line.line_total_paisa)}</Td>
      <Td className="align-top">
        {isFirstOfOrder ? (
          <div className="space-y-1">
            <Badge tone={isReturnBranch(order.status as OrderStatus) ? 'warning' : 'success'}>
              {ORDERSTATUSLABEL[order.status as OrderStatus] ?? order.status}
            </Badge>
            <button
              type="button"
              className="block font-body text-body-xs text-forest underline-offset-2 hover:underline"
              onClick={onOpenHistory}
            >
              View history
            </button>
          </div>
        ) : null}
      </Td>
      <Td className="align-top">
        {isFirstOfOrder ? (
          <NextStepActions
            order={order}
            next={next}
            onTransition={onTransition}
            onOpenDecision={onOpenDecision}
          />
        ) : null}
      </Td>
    </tr>
  )
}

function EmptyOrderRow({
  order,
  onTransition,
  onOpenHistory,
  onOpenDecision
}: {
  readonly order: OrderListDto
  readonly onTransition: (orderId: string, nextStatus: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onOpenHistory: () => void
  readonly onOpenDecision: (decision: 'accept' | 'reject') => void
}): JSX.Element {
  const next = pickNextForward(order.status)
  return (
    <tr className="border-t border-border">
      <Td className="whitespace-nowrap text-text-secondary">{formatDate(order.placed_at)}</Td>
      <Td>{customerName(order)}</Td>
      <Td className="text-text-secondary">—</Td>
      <Td className="text-right text-text-secondary">—</Td>
      <Td className="text-right text-text-secondary">—</Td>
      <Td>
        <div className="space-y-1">
          <Badge tone={isReturnBranch(order.status as OrderStatus) ? 'warning' : 'success'}>
            {ORDERSTATUSLABEL[order.status as OrderStatus] ?? order.status}
          </Badge>
          <button
            type="button"
            className="block font-body text-body-xs text-forest underline-offset-2 hover:underline"
            onClick={onOpenHistory}
          >
            View history
          </button>
        </div>
      </Td>
      <Td>
        <NextStepActions
          order={order}
          next={next}
          onTransition={onTransition}
          onOpenDecision={onOpenDecision}
        />
      </Td>
    </tr>
  )
}

/** "Next step" column body — shared between LineRow + EmptyOrderRow.
 *  `return_pending` orders branch to Accept/Reject (opens the review
 *  modal); everything else stays on the forward-only "Mark X" CTA. */
function NextStepActions({
  order,
  next,
  onTransition,
  onOpenDecision
}: {
  readonly order: OrderListDto
  readonly next: OrderStatus | null
  readonly onTransition: (orderId: string, nextStatus: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onOpenDecision: (decision: 'accept' | 'reject') => void
}): JSX.Element | null {
  if (order.status === ORDERSTATUS.RETURNPENDING && order.active_return_id !== null) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primary" onClick={() => onOpenDecision('accept')}>
          Accept
        </Button>
        <Button size="sm" variant="secondary" onClick={() => onOpenDecision('reject')}>
          Reject
        </Button>
      </div>
    )
  }
  if (next !== null) {
    return (
      <Button size="sm" variant="secondary" onClick={() => void onTransition(order.id, next)}>
        Mark {ORDERSTATUSLABEL[next]}
      </Button>
    )
  }
  return null
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
